import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { L } from "./shared";
import { Sparkles, Loader2, CheckCircle2, Trash2, RefreshCw, Save } from "lucide-react";

const ic = "w-full bg-navy border border-white/20 text-white p-2.5 text-sm focus:border-gold focus:outline-none";
const TEXT_FIELDS = [
  { key: "mission", label: "Mission", hint: "What mission does this help the customer accomplish?", rows: 2 },
  { key: "problem_solved", label: "Problem Solved", hint: "What business problem does this solve?", rows: 3 },
  { key: "ideal_customer", label: "Ideal Customer", hint: "Who is this built for?", rows: 3 },
  { key: "expected_transformation", label: "Expected Transformation", hint: "Who the buyer BECOMES — beyond the deliverable.", rows: 2 },
  { key: "ai_recommendations", label: "AIPOS Recommendation", hint: "When should AIPOS recommend this product?", rows: 2 },
];
const LIST_FIELDS = [
  { key: "outcomes", label: "Outcomes", hint: "One result per line — what success looks like." },
  { key: "deliverables", label: "Deliverables", hint: "One item per line — exactly what they receive." },
  { key: "success_metrics", label: "Success Metrics", hint: "One per line — how they know it worked." },
];
const ALL_KEYS = [...TEXT_FIELDS.map((f) => f.key), ...LIST_FIELDS.map((f) => f.key)];

const toLines = (v) => (Array.isArray(v) ? v.join("\n") : (v || ""));
const fromLines = (v) => (v || "").split("\n").map((s) => s.trim()).filter(Boolean);

export function SolutionFactory({ templateId, title }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [published, setPublished] = useState({});
  const [publishedAt, setPublishedAt] = useState(null);
  const [draft, setDraft] = useState(null); // {status, fields, provider, generated_at}
  const [fields, setFields] = useState({}); // editable working copy

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/templates/${templateId}/solution`).then(({ data }) => {
      setPublished(data.published || {});
      setPublishedAt(data.published_at || null);
      setDraft(data.draft || null);
      if (data.draft?.fields) setFields(data.draft.fields);
      else setFields(data.published || {});
    }).catch(() => {}).finally(() => setLoading(false));
  }, [templateId]);
  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setFields((p) => ({ ...p, [k]: v }));

  const generate = async (mode) => {
    setBusy("gen");
    try {
      const { data } = await api.post(`/templates/${templateId}/generate-solution`, { mode });
      setDraft(data.draft);
      setFields(data.draft.fields || {});
      toast.success(`AIPOS drafted a Solution Profile (${data.draft.provider || "AI"}). Review, edit & publish.`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const collect = () => {
    const out = {};
    TEXT_FIELDS.forEach((f) => { out[f.key] = fields[f.key] || ""; });
    LIST_FIELDS.forEach((f) => { out[f.key] = Array.isArray(fields[f.key]) ? fields[f.key] : fromLines(fields[f.key]); });
    return out;
  };

  const saveDraft = async () => {
    setBusy("save");
    try {
      const { data } = await api.put(`/templates/${templateId}/solution-draft`, { fields: collect() });
      setDraft(data.draft);
      toast.success("Draft saved.");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const publish = async () => {
    setBusy("pub");
    try {
      await api.post(`/templates/${templateId}/solution/publish`, { fields: collect() });
      toast.success("Solution Profile published — now live on the product page.");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const discard = async () => {
    if (!window.confirm("Discard this AI draft? Your published Solution Profile is not affected.")) return;
    setBusy("del");
    try {
      await api.delete(`/templates/${templateId}/solution-draft`);
      toast.success("Draft discarded.");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  if (loading) return <div className="text-gray-500 text-sm py-4" data-testid="solution-loading">Loading Solution Profile…</div>;

  const hasDraft = draft && draft.status === "draft";
  const isPublished = !!publishedAt;

  return (
    <div className="space-y-4" data-testid="solution-factory">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-gray-400">
          {isPublished
            ? <span className="inline-flex items-center gap-1.5 text-green-300" data-testid="solution-published-badge"><CheckCircle2 size={13} /> Published {new Date(publishedAt).toLocaleDateString()}</span>
            : <span className="text-gray-500" data-testid="solution-unpublished-badge">No published Solution Profile yet</span>}
          {hasDraft && <span className="ml-3 inline-flex items-center gap-1.5 text-gold" data-testid="solution-draft-badge"><Sparkles size={12} /> Unpublished draft</span>}
        </div>
        <div className="flex gap-2">
          <button type="button" disabled={!!busy} onClick={() => generate("generate")} data-testid="generate-solution-btn"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 bg-gold text-navy font-semibold hover:bg-gold-light disabled:opacity-50">
            {busy === "gen" ? <Loader2 className="animate-spin" size={13} /> : <Sparkles size={13} />} Generate Solution
          </button>
          {isPublished && (
            <button type="button" disabled={!!busy} onClick={() => generate("refresh")} data-testid="refresh-solution-btn"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-gold/40 text-gold hover:bg-gold/10 disabled:opacity-50">
              {busy === "gen" ? <Loader2 className="animate-spin" size={13} /> : <RefreshCw size={13} />} Refresh
            </button>
          )}
        </div>
      </div>

      <p className="text-[11px] text-gray-500 border border-white/10 bg-navy/40 p-2.5">
        AIPOS drafts a consultative Solution Profile from this product's data. Nothing goes live until you
        <span className="text-gold"> Approve &amp; Publish</span>. Editing here never touches the published page.
      </p>

      <div className="space-y-4" data-testid="solution-fields">
        {TEXT_FIELDS.map((ff) => (
          <L key={ff.key} label={ff.label}>
            <textarea rows={ff.rows} className={ic} value={fields[ff.key] || ""} onChange={(e) => setField(ff.key, e.target.value)}
              placeholder={ff.hint} data-testid={`solution-${ff.key}`} />
          </L>
        ))}
        {LIST_FIELDS.map((ff) => (
          <L key={ff.key} label={`${ff.label} (one per line)`}>
            <textarea rows={4} className={ic} value={toLines(fields[ff.key])} onChange={(e) => setField(ff.key, fromLines(e.target.value))}
              placeholder={ff.hint} data-testid={`solution-${ff.key}`} />
          </L>
        ))}
      </div>

      <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-white/10">
        {hasDraft && (
          <button type="button" disabled={!!busy} onClick={discard} data-testid="discard-solution-btn"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-red-400/40 text-red-300 hover:bg-red-400/10 disabled:opacity-50">
            {busy === "del" ? <Loader2 className="animate-spin" size={13} /> : <Trash2 size={13} />} Discard draft
          </button>
        )}
        <button type="button" disabled={!!busy} onClick={saveDraft} data-testid="save-solution-draft-btn"
          className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-white/20 text-white hover:bg-white/5 disabled:opacity-50">
          {busy === "save" ? <Loader2 className="animate-spin" size={13} /> : <Save size={13} />} Save draft
        </button>
        <button type="button" disabled={!!busy} onClick={publish} data-testid="publish-solution-btn"
          className="inline-flex items-center gap-1.5 text-xs px-3 py-2 bg-green-500/90 text-navy font-semibold hover:bg-green-400 disabled:opacity-50">
          {busy === "pub" ? <Loader2 className="animate-spin" size={13} /> : <CheckCircle2 size={13} />} Approve &amp; Publish
        </button>
      </div>
    </div>
  );
}
